import Collapsible from 'react-collapsible';
import * as charges from '../config/charges.json';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import * as _ from 'lodash';
import { Fleet } from '../models/cars.model';
import discounts from '../config/discount';
import { get } from 'lodash';
import { getAvailableKMRanges } from '../utils/rangeRules';

interface CarDetailsProps {
    idx: number,
    vehicle: Fleet,
    pickupDate: Date,
    dropoffDate: Date,
    selectedPickup: string,
    selectedDropoff: string,
    isSelfDrive: Boolean,
    isBikeRental?: Boolean
}
function CarDetails(props: CarDetailsProps) {
    const [selectedKMs, setSelectedKMs] = useState<string>(props.vehicle.KMIncluded![0]);
    const [isFlipped, setIsFlipped] = useState<boolean>(false);
    const [activeImgIdx, setActiveImgIdx] = useState<number>(0);

    const convertTo24HourFormat = (time: any) => {
        // Extract the hour and period (AM/PM)
        let [hour, period] = time.split(' ');
        hour = parseInt(hour);

        if (period === 'AM') {
            if (hour === 12) {
                // 12 AM is 0 in 24-hour format
                return 0;
            }
            return hour; // AM hours stay the same, except 12 AM
        } else if (period === 'PM') {
            if (hour === 12) {
                // 12 PM is 24 in 24-hour format
                return 24;
            }
            return hour + 12; // Add 12 to PM hours except 12 PM
        }
    }
    const getDays = () => {
        if (props.isSelfDrive) {
            const startHour = convertTo24HourFormat(props.vehicle.StartTime); // 9 AM
            const msInADay = 24 * 60 * 60 * 1000; // milliseconds in a day

            // Helper function to calculate the hours difference
            const getHoursDifference = (date1: Date, date2: Date): number => {
                return Math.abs(date1.getTime() - date2.getTime()) / (1000 * 60 * 60); // Milliseconds to hours
            }

            // Adjust start date for time before 9 AM
            let adjustedStartDate = new Date(props.pickupDate);
            let startDayFraction = 0;

            if (props.pickupDate.getHours() < startHour) {
                // Calculate time difference between start time and 9 AM
                const nineAMStartDate = new Date(props.pickupDate);
                nineAMStartDate.setHours(startHour, 0, 0, 0); // Set to 9 AM on the start date
                const hoursBefore9AM = getHoursDifference(props.pickupDate, nineAMStartDate);

                // If the time before 9 AM is <= 12 hours, it's 0.5 days, otherwise it's 1 day
                startDayFraction = props.vehicle.HalfDayCharges.length > 0 && hoursBefore9AM <= 12 ? 0.5 : 1;

                // Adjust the start date to 9 AM on the previous day
                //adjustedStartDate.setDate(adjustedStartDate.getDate() - 1);
                adjustedStartDate.setHours(startHour, 0, 0, 0);
            } else {
                // No need to adjust if the time is after 9 AM, set the time to 9 AM
                adjustedStartDate.setHours(startHour, 0, 0, 0);
            }

            // Adjust end date for time after 9 AM
            let adjustedEndDate = new Date(props.dropoffDate);
            let endDayFraction = 0;

            if (props.dropoffDate.getHours() > startHour) {
                // Calculate time difference between 9 AM and end time
                const nineAMEndDate = new Date(props.dropoffDate);
                nineAMEndDate.setHours(startHour, 0, 0, 0); // Set to 9 AM on the end date
                const hoursAfter9AM = getHoursDifference(nineAMEndDate, props.dropoffDate);

                // If the time after 9 AM is <= 12 hours, it's 0.5 days, otherwise it's 1 day
                endDayFraction = props.vehicle.HalfDayCharges.length > 0 && hoursAfter9AM <= 12 ? 0.5 : 1;

                // Adjust the end date to 9 AM the next day
                adjustedEndDate.setHours(startHour, 0, 0, 0);
                //adjustedEndDate.setDate(adjustedEndDate.setDate() + 1);
            } else {
                // No need to adjust if the time is before 9 AM, set the time to 9 AM
                adjustedEndDate.setHours(startHour, 0, 0, 0);
            }

            // Calculate full days (from 9 AM to 9 AM) between the adjusted dates
            const diffInMs = adjustedEndDate.getTime() - adjustedStartDate.getTime();
            const fullDays = Math.floor(diffInMs / msInADay);

            // Return the total days (full days + any fractions calculated)
            const totalDays = fullDays + startDayFraction + endDayFraction;
            return totalDays < 1 ? 1 : totalDays;
        } else {
            if (props.dropoffDate.toLocaleDateString() == props.pickupDate.toLocaleDateString())
                return 1
            const diffTime = Math.abs(props.dropoffDate.getTime() - props.pickupDate.getTime());
            const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
            return days
        }
    }

    const isSelfDriveCar = Boolean(props.isSelfDrive && !props.isBikeRental);
    const rangeInfo = getAvailableKMRanges(props.vehicle.KMIncluded, getDays(), isSelfDriveCar);

    const calculateCharges = () => {
        let pricingIndex = props.vehicle.KMIncluded.indexOf(selectedKMs);
        if (pricingIndex < 0) pricingIndex = 0;
        const splitDays = getDays().toString().split('.'),
            days = parseInt(splitDays[0]);

        return days * (props.vehicle.Charges[pricingIndex] || 0) + (splitDays.length > 1 ? (props.vehicle.HalfDayCharges[pricingIndex] || 0) : 0);
    }
    const getVehicleCharges = () => {
        let selectedIndex = props.vehicle.KMIncluded.indexOf(selectedKMs);
        if (selectedIndex < 0) selectedIndex = 0;
        return {
            charges: props.vehicle.Charges[selectedIndex] || 0,
            kmPerDay: selectedKMs || props.vehicle.KMIncluded[0] || '150',
            extraKMCharges: props.vehicle.ExtraKMCharges[selectedIndex] || 0
        }
    }
    const getTotal = () => {
        let total = { total: 0, actualCharges: 0 },
            pickUpCharges = props.selectedPickup ? charges[props.selectedPickup as keyof typeof charges]['Convenience Fee'] : 0,
            dropOffCharges = props.selectedDropoff ? charges[props.selectedDropoff as keyof typeof charges]['Convenience Fee'] : 0,
            platformCharges = (props.selectedPickup && props.selectedDropoff) ? charges[props.selectedPickup as keyof typeof charges]['Platform Charges'] + charges[props.selectedDropoff as keyof typeof charges]['Platform Charges'] : 0;

        total = props.isSelfDrive ? getSelfDriveCharges(pickUpCharges, dropOffCharges, platformCharges) : { actualCharges: calculateCharges() + platformCharges + getOvernightCharges(), total: calculateCharges() + platformCharges + getOvernightCharges() };
        return total;
    }

    const getSelfDriveCharges = (pickUpCharges: number, dropOffCharges: number, platformCharges: number) => {

        const isDiscountApplicable = get(props.vehicle, 'isDiscountApplicable', false);
        let days = getDays();
        let discount = {
            "DiscountPercent": 0,
            "PickDropDiscountPercent": 0
        };
        if (days <= 1) discount = discounts["SingleDay"];
        else if (days <= 3) discount = discounts["UptoThreeDays"];
        else if (days <= 6) discount = discounts["UptoSixDays"];
        else if (days > 6) discount = discounts["MoreThanSixDays"];

        let actualCharges = calculateCharges() + pickUpCharges + dropOffCharges + platformCharges;
        let charges = isDiscountApplicable ? calculateCharges() - (Math.round((calculateCharges() * discount.DiscountPercent) / 100)) : calculateCharges();
        let pickUpDiscount = Math.round((pickUpCharges * discount.PickDropDiscountPercent) / 100);
        let dropOffDiscount = Math.round((dropOffCharges * discount.PickDropDiscountPercent) / 100);
        pickUpCharges = isDiscountApplicable ? pickUpCharges - pickUpDiscount : pickUpCharges;
        dropOffCharges = isDiscountApplicable ? dropOffCharges - dropOffDiscount : dropOffCharges;
        let pickDropCharges = pickUpCharges + dropOffCharges;

        let total = charges + pickDropCharges + platformCharges;
        return { total, actualCharges };
    }

    const isOvernightChargeApplicable = () => {
        return !props.isSelfDrive && getDays() > 1;
    }
    const getOvernightCharges = () => {
        if (isOvernightChargeApplicable()) {
            return props.selectedPickup ? charges[props.selectedPickup as keyof typeof charges]['Overnight Charges'] * (getDays() - 1) : 0;
        }
        return 0;
    }
    const getPickupCharges = () => {
        return props.selectedPickup && charges[props.selectedPickup as keyof typeof charges]['Convenience Fee'];
    }
    const getDropoffCharges = () => {
        return props.selectedDropoff && charges[props.selectedDropoff as keyof typeof charges]['Convenience Fee'];
    }
    const getMessage = () => {
        if (props.pickupDate && props.dropoffDate && props.selectedPickup && props.selectedDropoff) {
            const { total, actualCharges } = getTotal();
            const discountAmount = actualCharges - total;

            let message = `
Hello AapliGaadi,
I am interested in ${props.vehicle.Brand}, ${props.vehicle.Name}.\n
Booking request details:\n
Start Date & Location: ${moment(props.pickupDate).format('LLLL')} (${props.selectedPickup})
End Date & Location: ${moment(props.dropoffDate).format('LLLL')} (${props.selectedDropoff})\n
Total Days: ${getDays()}
Total KM(s) included: ${getVehicleCharges().kmPerDay.toString() == 'Unlimited' ? 'Unlimited' : parseInt(getVehicleCharges().kmPerDay) * getDays()} KMs
Fleet Charges: ₹${calculateCharges()}/-
Pickup Charges: ₹${getPickupCharges()}/-
Dropoff Charges: ₹${getDropoffCharges()}/-
Platform Charges: ₹${charges[props.selectedPickup as keyof typeof charges]['Platform Charges'] + charges[props.selectedDropoff as keyof typeof charges]['Platform Charges']}/-\n
`;

            if (discountAmount > 0) {
                message += `Original Total: ₹${actualCharges}/-\n`;
                message += `Discount Applied: -₹${discountAmount}/-\n`;
            }

            message += `Total Payable Amount: ₹${total}/-\n\n`;

            if (props.isSelfDrive) {
                message += `Please Note:\n`; 
                message += `- Additional Refundable Security Deposit of ₹${props.vehicle.SecurityDeposit} will be charged!\n`;
                message += `- Additional KM Charges (if any): ₹${getVehicleCharges().extraKMCharges}/- per KM\n`;
                if (discountAmount > 0) {
                    message += `- Total Discount Applied: ₹${discountAmount}/-\n`;
                }
            }

            return encodeURIComponent(message);
        }
        return null;
    }

    useEffect(() => {
        const { allowedKMs, isRestricted } = getAvailableKMRanges(props.vehicle.KMIncluded, getDays(), isSelfDriveCar);
        if (isRestricted) {
            if (selectedKMs !== '') setSelectedKMs('');
        } else if (allowedKMs.length > 0) {
            if (!selectedKMs || !allowedKMs.includes(selectedKMs)) {
                setSelectedKMs(allowedKMs[0]);
            }
        }
        setActiveImgIdx(0);
        setIsFlipped(false);
    }, [props.vehicle, props.pickupDate, props.dropoffDate, isSelfDriveCar, selectedKMs]);

    const getVehicleSlug = () => {
        return _.kebabCase(`${props.vehicle.Brand}-${props.vehicle.Name}-${props.vehicle.isSelfDrive ? 'self-drive' : 'with-driver'}`);
    };

    const { serviceType, location } = useParams();

    const getDetailsUrl = () => {
        const slug = getVehicleSlug();
        const params = new URLSearchParams({
            pickupDate: props.pickupDate ? props.pickupDate.toISOString() : '',
            dropoffDate: props.dropoffDate ? props.dropoffDate.toISOString() : '',
            pickup: props.selectedPickup || '',
            dropoff: props.selectedDropoff || '',
            kms: selectedKMs || ''
        });
        return `/services/${serviceType}/${location}/${slug}?${params.toString()}`;
    };

    return (
        <div className="car-wrap rounded" key={props.idx}><div className="row">
            {(() => {
                const imageList = props.vehicle.Images && props.vehicle.Images.length > 0 ? props.vehicle.Images : [props.vehicle.Image];
                return (
                    <div className="col-md-4 d-flex flex-column align-items-center justify-content-center" style={{ padding: '2%', position: 'relative' }}>
                        <div className="vehicle-image-slider w-100" style={{ position: 'relative', overflow: 'hidden', borderRadius: '12px' }}>
                            <img 
                                className='img' 
                                src={`${process.env.PUBLIC_URL}/image/${props.isBikeRental ? 'bikes' : 'cars'}/${imageList[activeImgIdx]}`} 
                                style={{ width: '100%', height: 'auto', display: 'block', transition: 'all 0.3s ease-in-out' }} 
                                alt={`${props.vehicle.Name} - View ${activeImgIdx + 1}`} 
                            />
                            
                            {imageList.length > 1 && (
                                <>
                                    {/* Left Arrow Button */}
                                    <button 
                                        type="button" 
                                        className="slider-arrow left-arrow"
                                        onClick={() => setActiveImgIdx((prev) => (prev === 0 ? imageList.length - 1 : prev - 1))}
                                        aria-label="Previous image"
                                    >
                                        &#10094;
                                    </button>
                                    
                                    {/* Right Arrow Button */}
                                    <button 
                                        type="button" 
                                        className="slider-arrow right-arrow"
                                        onClick={() => setActiveImgIdx((prev) => (prev === imageList.length - 1 ? 0 : prev + 1))}
                                        aria-label="Next image"
                                    >
                                        &#10095;
                                    </button>
                                    
                                    {/* Dot Indicators */}
                                    <div className="slider-dots-container">
                                        {imageList.map((_, idx) => (
                                            <span 
                                                key={idx} 
                                                className={`slider-dot ${idx === activeImgIdx ? 'active' : ''}`}
                                                onClick={() => setActiveImgIdx(idx)}
                                            />
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                        <p style={{ textAlign: 'center', marginTop: '10px' }}>
                            <img style={{ height: '20px' }} src="/icons/location.png" alt="Hub" />
                            &nbsp;&nbsp;{props.vehicle.Hub}&nbsp;&nbsp;
                        </p>
                    </div>
                );
            })()}
            
            <div className="col-md-8" style={{ padding: '2%' }}>
                <div className={`flip-card ${isFlipped ? 'flipped' : ''}`} style={{ width: '100%', minHeight: '260px' }}>
                    <div className="flip-card-inner">
                        {/* Front Face: Specifications Sheet */}
                        <div className="flip-card-front d-flex flex-column justify-content-between text-left" style={{ position: 'relative', backfaceVisibility: 'hidden', width: '100%', height: '100%' }}>
                            <div className='text w-100 p-0'>
                                <h2 className="mb-0" style={{ fontSize: '22px' }}>{props.vehicle.Brand}, {props.vehicle.Name}</h2>
                                <div className="mt-2 mb-3">
                                    Select Daily range: {rangeInfo.isRestricted ? (
                                        <>
                                            <select disabled value="">
                                                <option value="">Restricted</option>
                                            </select> KMs
                                            <div className="text-danger small font-weight-bold mt-1">
                                                ⚠️ {rangeInfo.restrictionMessage}
                                            </div>
                                        </>
                                    ) : (
                                        <select disabled={rangeInfo.allowedKMs.length <= 1} value={selectedKMs} onChange={(e) => setSelectedKMs(e.target.value)}>
                                            {rangeInfo.allowedKMs.map(km => (
                                                <option key={km} value={km}>{km}</option>
                                            ))}
                                        </select>
                                    )} {!rangeInfo.isRestricted && 'KMs'}
                                </div>
                                <div className="row">
                                    <div className='col-6 col-md-4 spec-item' style={{ padding: '5px' }}>
                                        <img src="/icons/fuel.svg" alt="Fuel" />
                                        <span>{props.vehicle.Details.Fuel}</span>
                                    </div>
                                    <div className='col-6 col-md-4 spec-item' style={{ padding: '5px' }}>
                                        <img src="/icons/transmission.png" alt="Transmission" />
                                        <span>{props.vehicle.Details.Transmission}</span>
                                    </div>
                                    <div className='col-6 col-md-4 spec-item' style={{ padding: '5px' }}>
                                        <img src="/icons/luggage.svg" alt="Luggage" />
                                        <span>{props.vehicle.Details.LuggageCapacity} bags</span>
                                    </div>
                                    <div className='col-6 col-md-4 spec-item' style={{ padding: '5px' }}>
                                        <img src="/icons/seat.svg" alt="Seating" />
                                        <span>{props.vehicle.Details.SeatingCapacity} Seater</span>
                                    </div>
                                    <div className='col-6 col-md-4 spec-item' style={{ padding: '5px' }}>
                                        <img src="/icons/milage.png" alt="Mileage" />
                                        <span>{props.vehicle.Details['Avg/Ltr']} kmpl</span>
                                    </div>
                                    <div className='col-6 col-md-4 spec-item' style={{ padding: '5px' }}>
                                        <img src="/icons/price.png" alt="Price" />
                                        <span>
                                            <b><u style={{ borderBottom: '1px dotted #000', textDecoration: 'none' }}>{getVehicleCharges().charges}</u></b>/ {props.vehicle.UOM}
                                        </span>
                                    </div>
                                    <div className='col-6 col-md-4 spec-item' style={{ padding: '5px' }}>
                                        <img src="/icons/road.png" alt="Included KMs" />
                                        <span>
                                            <b><u style={{ borderBottom: '1px dotted #000', textDecoration: 'none' }}>{getVehicleCharges().kmPerDay}</u></b> KMs/Day
                                        </span>
                                    </div>
                                    <div className='col-6 col-md-4 spec-item' style={{ padding: '5px' }}>
                                        <img src="/icons/extra.png" alt="Extra KM Charge" />
                                        <span>
                                            {getVehicleCharges().extraKMCharges > 0 ? <b><u style={{ borderBottom: '1px dotted #000', textDecoration: 'none' }}>{getVehicleCharges().extraKMCharges} Rs. /KM</u> </b> : 'N/A'}
                                        </span>
                                    </div>
                                    {
                                        props.isSelfDrive && <div className='col-6 col-md-4 spec-item' style={{ padding: '5px' }}>
                                            <img src="/icons/clock9.svg" alt="Timing" />
                                            <span>From {props.vehicle["StartTime"]} to {props.vehicle["EndTime"]}</span>
                                        </div>
                                    }
                                </div>
                            </div>
                            <div className="card-bottom-bar mt-3 pt-3 border-top d-flex align-items-center justify-content-between w-100">
                                <a href="#trip_details" className="price-display-link">
                                    {props.pickupDate && props.dropoffDate && props.selectedPickup && props.selectedDropoff ? (
                                        getTotal().actualCharges === getTotal().total ? (
                                            <>Total: {`${getTotal().total}/- Rs.`}</>
                                        ) : (
                                            <>Total: <s>{`${getTotal().actualCharges}/- Rs.`}</s>{` ${getTotal().total}/- Rs.`}</>
                                        )
                                    ) : 'Select pickup & drop points to see price.'}
                                </a>
                                {props.pickupDate && props.dropoffDate && props.selectedPickup && props.selectedDropoff && (
                                    <div className="d-flex align-items-center mobile-full-width" style={{ gap: '10px', minWidth: '180px' }}>
                                        {rangeInfo.isRestricted ? (
                                            <button 
                                                disabled 
                                                className="btn btn-secondary py-2 px-3 text-center flex-grow-1" 
                                                style={{ opacity: 0.65, cursor: 'not-allowed' }}
                                                title="1-day bookings are restricted"
                                            >
                                                Restricted
                                            </button>
                                        ) : (
                                            <Link 
                                                to={getDetailsUrl()} 
                                                className="btn btn-primary py-2 px-3 text-center flex-grow-1"
                                            >
                                                View Details
                                            </Link>
                                        )}
                                        <button 
                                            type="button"
                                            onClick={() => setIsFlipped(true)} 
                                            className="btn btn-outline-primary py-2 px-3 text-center flex-grow-1"
                                        >
                                            View Price Breakup
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Back Face: Pricing details */}
                        <div className="flip-card-back d-flex flex-column justify-content-between text-left" style={{ position: 'absolute', backfaceVisibility: 'hidden', transform: 'rotateY(180deg)', width: '100%', height: '100%', left: 0, top: 0 }}>
                            <div className="w-100 flex-grow-1" style={{ overflowY: 'auto' }}>
                                <h3 className="h6 font-weight-bold text-dark border-bottom pb-2 mb-2 d-flex justify-content-between align-items-center">
                                    <span>Pricing Breakup ({props.vehicle.Brand} {props.vehicle.Name})</span>
                                    <button 
                                        type="button"
                                        className="btn btn-sm btn-light p-0" 
                                        onClick={() => setIsFlipped(false)} 
                                        style={{ borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #cbd5e1' }}
                                    >
                                        ✕
                                    </button>
                                </h3>
                                <ul className="pl-3 mb-0" style={{ fontSize: '13px', color: 'var(--text-dark)', listStyleType: 'disc' }}>
                                    <li className="mb-2">Number of Days: {getDays()} Day(s) </li>
                                    <li className="mb-2">Total KMs Included: {getVehicleCharges().kmPerDay.toString() === 'Unlimited' ? 'Unlimited' : parseInt(getVehicleCharges().kmPerDay) * getDays()} KMs</li>
                                    <li className="mb-2">Fleet Charges: ₹{calculateCharges()}</li>
                                    {props.isSelfDrive && (
                                        <li className="mb-2">
                                            Convenience Fee:
                                            <ul className="pl-3" style={{ listStyleType: 'circle' }}>
                                                <li>Pick up charges: ₹{getPickupCharges()}</li>
                                                <li>Drop off charges: ₹{getDropoffCharges()}</li>
                                            </ul>
                                        </li>
                                    )}
                                    {isOvernightChargeApplicable() && <li className="mb-2">Overnight charges: ₹{getOvernightCharges()}</li>}
                                    <li className="mb-2">
                                        Platform Handling Fees: ₹{props.selectedPickup && props.selectedDropoff && charges[props.selectedPickup as keyof typeof charges]['Platform Charges'] + charges[props.selectedDropoff as keyof typeof charges]['Platform Charges']}
                                    </li>
                                    {props.isSelfDrive && getTotal().actualCharges - getTotal().total > 0 && (
                                        <li className="mb-2 text-success font-weight-bold">
                                            Discount Applied: -₹{getTotal().actualCharges - getTotal().total}
                                        </li>
                                    )}
                                </ul>
                            </div>
                            <div className="card-bottom-bar mt-3 pt-3 border-top d-flex align-items-center justify-content-between w-100">
                                <span className="price-display-link">
                                    Total: ₹{getTotal().total}/-
                                </span>
                                <div className="d-flex align-items-center mobile-full-width" style={{ gap: '10px', minWidth: '180px' }}>
                                    <Link 
                                        to={getDetailsUrl()} 
                                        className="btn btn-primary py-2 px-3 text-center flex-grow-1"
                                    >
                                        View Details
                                    </Link>
                                    <button 
                                        type="button"
                                        onClick={() => setIsFlipped(false)} 
                                        className="btn btn-outline-primary py-2 px-3 text-center flex-grow-1"
                                    >
                                        Show Specs
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </div>
    )
}

export default CarDetails;